import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, PenLine, CreditCard, ChevronDown, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { closingAccess, closingSign, closingPay } from "@/lib/api";

function DocCard({ doc, email, token, onSigned }) {
  const [open, setOpen] = useState(false);
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const signed = doc.signature_status === "SIGNED";
  const sign = async () => {
    if (sig.trim().length < 2) return toast.error("Type your full legal name to sign.");
    setBusy(true);
    try {
      const r = await closingSign(token, email, doc.document_type, sig.trim());
      toast.success(`Signed: ${doc.title}`);
      onSigned(r);
    } catch (e) { toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || "Could not sign."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`closing-doc-${doc.document_type}`} className="border border-[#1f1f1f] rounded-2xl bg-[#0b0b0c] overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} data-testid={`closing-doc-toggle-${doc.document_type}`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.02]">
        <div>
          <div className="text-white text-sm font-medium">{doc.title} <span className="text-zinc-500">v{doc.version}</span></div>
          <div className="mono-label text-[10px] mt-1 flex gap-3">
            {doc.requires_signature
              ? <span className={signed ? "text-emerald-300" : "text-amber-300"}>{signed ? "SIGNED" : "SIGNATURE REQUIRED"}</span>
              : <span className="text-cyan-300">ACKNOWLEDGE</span>}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="max-h-[300px] overflow-y-auto rounded-lg border border-[#171717] bg-black/40 p-4 text-zinc-300 text-[13px] whitespace-pre-wrap">
            {doc.content || "Content unavailable."}
          </div>
          {doc.requires_signature && !signed && (
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Input data-testid={`closing-sign-input-${doc.document_type}`} value={sig} onChange={(e) => setSig(e.target.value)}
                placeholder="Type full legal name to sign" className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-11" />
              <button data-testid={`closing-sign-btn-${doc.document_type}`} onClick={sign} disabled={busy}
                className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PenLine className="h-4 w-4" /> Sign</>}
              </button>
            </div>
          )}
          {signed && <div className="mt-3 text-emerald-300 text-xs inline-flex items-center gap-1"><Check className="h-3 w-3" /> Executed</div>}
        </div>
      )}
    </div>
  );
}

export default function Closing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);

  const open = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter the email for this engagement.");
    setBusy(true);
    try { setD(await closingAccess(token, email.trim())); }
    catch (e) { toast.error(e?.response?.data?.detail || "This closing is not available."); }
    finally { setBusy(false); }
  };
  const refresh = async () => { try { setD(await closingAccess(token, email.trim())); } catch {} };
  const pay = async () => {
    setBusy(true);
    try {
      const r = await closingPay(token, email.trim(), window.location.origin);
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Payment could not start."); }
    finally { setBusy(false); }
  };

  const Header = () => (
    <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
      <Seo noindex path="/closing" title="Secure Closing · AIROTECH" />
      <div className="max-w-4xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <button onClick={() => navigate("/")}><img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto" /></button>
        <div className="mono-label text-cyan-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> SECURE CLOSING</div>
      </div>
    </header>
  );

  if (!d) return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-xl mx-auto px-6 pt-20">
        <div className="mono-label text-cyan-400 mb-6">AIROTECH CLOSING</div>
        <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">Complete your engagement.</h1>
        <p className="mt-5 text-zinc-400">Confirm your email to review, sign and pay for your agreement.</p>
        <div className="mt-8 space-y-3">
          <Label className="mono-label text-zinc-400">Your email</Label>
          <Input data-testid="closing-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && open()} className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-12" placeholder="you@company.com" />
        </div>
        <button data-testid="closing-open" onClick={open} disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-white text-black px-7 py-4 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Open Closing <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );

  const allSigned = (d.signatures || []).every((s) => s.status === "SIGNED");
  const needsSign = (d.signatures || []).some((s) => s.status !== "SIGNED");
  const canPay = d.closing_sequence === "pay_then_sign" || allSigned || !needsSign;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mono-label text-cyan-400 mb-2">{d.service_name}</div>
        <h1 data-testid="closing-title" className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">Review, sign & pay.</h1>
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div><span className="text-zinc-500 mono-label">TOTAL</span><div className="text-white text-lg font-medium" data-testid="closing-total">{d.total_amount} {(d.currency || "").toUpperCase()}</div></div>
          <div><span className="text-zinc-500 mono-label">PAYMENT</span><div className="text-white" data-testid="closing-payment-summary">{d.payment_summary}</div></div>
          <div><span className="text-zinc-500 mono-label">STATE</span><div className="text-cyan-300 mono-label">{d.state}</div></div>
        </div>
        <div className="mt-2 text-zinc-600 text-[10px] mono-label break-all">PACKAGE HASH {d.package_hash}</div>

        <div className="mt-8 space-y-3">
          {d.documents.map((doc) => <DocCard key={doc.document_type} doc={doc} email={email} token={token} onSigned={refresh} />)}
        </div>

        <div className="mt-8 border-t border-[#141414] pt-8">
          {needsSign && d.closing_sequence !== "pay_then_sign" && (
            <p className="text-amber-300 text-sm mb-4 inline-flex items-center gap-2"><PenLine className="h-4 w-4" /> Please sign all required documents before payment.</p>
          )}
          <button data-testid="closing-pay" onClick={pay} disabled={busy || !canPay || d.state === "PAID" || d.state === "ACTIVATED"}
            className="inline-flex items-center gap-2 bg-white text-black px-7 py-4 rounded-full mono-label hover:bg-cyan-400 disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> Continue to Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
}
